import { apiOrigin } from '@/services/apiClient';
import { normalizePortalTenant, storePortalTenant } from './portalTenantContext';
import { expirePortalSession, portalSessionGeneration } from './portalSessionSync';

/**
 * The EXTERNAL client-portal API client (M11).
 *
 * Deliberately NOT the staff `apiClient`:
 *
 *  - it never reads or sends a staff bearer token;
 *  - it authenticates with an httpOnly cookie scoped to `/api/v1/portal`, so
 *    the browser cannot attach it to a staff request;
 *  - every non-GET carries the CSRF token issued at sign-in.
 *
 * Sharing the staff client would mean one code path could send a staff token to
 * a portal route or a portal cookie to a staff route. Two clients, no overlap.
 */

const PORTAL_BASE = `${apiOrigin}/api/v1/portal`;
const CSRF_HEADER = 'x-portal-csrf';
/**
 * The CSRF token lives in sessionStorage, not localStorage: it should die with
 * the tab. The SESSION itself is an httpOnly cookie JavaScript cannot read, so
 * this value alone grants nothing.
 */
const CSRF_STORAGE_KEY = 'helios.portal.csrf';

export class PortalApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'PortalApiError';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isOffline(): boolean {
    return this.status === 0;
  }
}

/** A reserved original is not attachable until metadata confirms it is AVAILABLE. */
export class PortalInvoiceUploadUncertainError extends PortalApiError {
  constructor(
    readonly invoiceEvidenceId: string,
    readonly projectId: string,
    message: string,
    status: number,
  ) {
    super(message, status);
    this.name = 'PortalInvoiceUploadUncertainError';
  }
}

export function readPortalCsrfToken(): string | null {
  try {
    return window.sessionStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storePortalCsrfToken(token: string): void {
  try {
    window.sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    /* private browsing — the request will fail honestly with a 403 */
  }
}

export function clearPortalCsrfToken(): void {
  try {
    window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

interface Envelope<T> {
  success?: boolean;
  data?: T;
  error?: { message?: string };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const generation = portalSessionGeneration();
  const method = (init?.method ?? 'GET').toUpperCase();
  const headers = new Headers(init?.headers);
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = readPortalCsrfToken();
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }

  let response: Response;
  try {
    response = await fetch(`${PORTAL_BASE}${path}`, {
      ...init,
      method,
      // The whole point: the session cookie rides along, nothing else does.
      credentials: 'include',
      headers,
    });
  } catch (error) {
    throw new PortalApiError(error instanceof Error ? error.message : 'Network request failed', 0);
  }

  // A 204 or an error page with no JSON body is normal, not exceptional.
  const body = await (response.json() as Promise<Envelope<T>>).catch(() => null);
  // Identity-changing responses set the shared cookie and must finish their
  // own verification/logout lifecycle. All data responses belong only to the
  // session generation that issued them, including imperative private URLs.
  if (
    path !== '/auth/verify' &&
    path !== '/auth/sign-out' &&
    generation !== portalSessionGeneration()
  ) {
    throw new PortalApiError('Your portal session changed. Sign in again to continue.', 401);
  }

  if (!response.ok || body?.success === false) {
    // A current authenticated 401 is evidence that cached private data is no
    // longer authorized. Public sign-in failures leave existing sessions alone.
    if (
      response.status === 401 &&
      path !== '/auth/verify' &&
      path !== '/auth/request-link' &&
      generation === portalSessionGeneration()
    ) {
      clearPortalCsrfToken();
      expirePortalSession();
    }
    throw new PortalApiError(
      body?.error?.message ?? response.statusText ?? 'Something went wrong',
      response.status,
    );
  }
  return (body?.data ?? body) as T;
}

/** Shared by portal-only feature clients; retains cookie, CSRF and identity-generation checks. */
export const portalRequest = request;

// ---------------------------------------------------------------------------
// Response contracts — mirror apps/api/src/modules/portal/portal.types.ts.
// Nothing here carries budget, margin, cost codes, vendors or internal notes,
// because the server never sends them.
// ---------------------------------------------------------------------------

export type PortalScheduleState = 'ON_TRACK' | 'WATCH' | 'BEHIND' | 'COMPLETE' | 'UNAVAILABLE';
export type PortalAccessLevel = 'VIEW' | 'APPROVE';

export interface PortalProjectSummary {
  id: string;
  name: string;
  projectNumber: string;
  accessLevel: PortalAccessLevel;
  percentComplete: number | null;
  scheduleState: PortalScheduleState;
  openSelectionCount: number;
  unreadMessageCount: number;
}

export interface PortalMe {
  contact: {
    id: string;
    name: string;
    email: string;
    clientAccountName: string;
    organizationName: string;
    participantType: PortalParticipantType;
    tenantName?: string;
    tenantSlug?: string;
    canManagePayments: boolean;
  };
  /** `preview` is true for a staff read-only "preview as contact" session. */
  session: { expiresAt: string; preview?: boolean };
  projects: PortalProjectSummary[];
}

export interface PortalMilestone {
  id: string;
  name: string;
  targetDate: string;
  status: string;
}

export interface PortalPhoto {
  shareId: string;
  title: string;
  sharedAt: string;
}

export interface PortalProjectStatus {
  id: string;
  name: string;
  projectNumber: string;
  location: string | null;
  percentComplete: number | null;
  scheduleState: PortalScheduleState;
  headline: string;
  upcomingMilestones: PortalMilestone[];
  recentPhotos: PortalPhoto[];
  lastUpdatedAt: string;
}

export interface PortalSelectionOption {
  id: string;
  name: string;
  description: string | null;
  totalPrice: string;
  leadTimeDays: number | null;
  isRecommended: boolean;
  overAllowanceAmount: string;
}

export interface PortalSelectionDecision {
  id: string;
  status: 'APPROVED' | 'DECLINED';
  selectionOptionId: string;
  selectionOptionName: string;
  overAllowanceAmount: string;
  comment: string | null;
  decidedAt: string;
  decidedBy: string;
  changeOrderNumber: string | null;
}

export interface PortalSelectionItem {
  id: string;
  itemNumber: string;
  title: string;
  category: string;
  description: string | null;
  allowanceAmount: string;
  status: 'OPEN' | 'DECIDED' | 'ORDERED' | 'INSTALLED';
  decisionDueDate: string | null;
  options: PortalSelectionOption[];
  decision: PortalSelectionDecision | null;
  canDecide: boolean;
}

export interface PortalSelectionList {
  items: PortalSelectionItem[];
  totalAllowance: string;
  totalDecidedOverAllowance: string;
}

export interface PortalDecisionLogEntry {
  id: string;
  itemTitle: string;
  optionName: string;
  status: 'APPROVED' | 'DECLINED';
  overAllowanceAmount: string;
  comment: string | null;
  decidedAt: string;
  decidedBy: string;
  changeOrderNumber: string | null;
}

export interface PortalMessage {
  id: string;
  bodyText: string;
  senderName: string;
  senderIsYou: boolean;
  createdAt: string;
}

export interface PortalDocument {
  shareId: string;
  title: string;
  category: string;
  sharedAt: string;
  downloadable: boolean;
}

export interface PortalVerifyResult {
  contact: { id: string; name: string; email: string };
  tenantSlug: string;
  csrfToken: string;
  expiresAt: string;
  destination?: '/portal/onboarding' | '/portal';
}

export interface PortalDownloadTicket {
  url: string;
  fileName: string;
  expiresAt: string;
}

export type PortalParticipantType = 'CLIENT' | 'SUBCONTRACTOR' | 'VENDOR';
export type PortalRenewalState = 'CURRENT' | 'DUE_60' | 'DUE_30' | 'EXPIRED' | 'MISSING';
export interface PortalWorkItem {
  id: string;
  kind: 'CONTRACT' | 'PURCHASE_ORDER';
  number: string;
  title: string;
  status: string;
  project: { id: string; number: string; name: string } | null;
  /** Null when the contract value has not been recorded; never shown as $0. */
  amount: string | null;
  effectiveDate: string | null;
  promisedDate: string | null;
  needByDate: string | null;
  expirationDate: string | null;
}
export interface PortalRequirement {
  id: string;
  kind: string;
  title: string;
  required: boolean;
  dueDate: string | null;
  expiryDate: string | null;
  status: string;
  note: string | null;
  reviewNote?: string | null;
  version: number;
  hasDocument: boolean;
  renewalState: PortalRenewalState;
  daysRemaining: number | null;
}
export interface PortalWorkList {
  items: PortalWorkItem[];
  total: number;
}
export type PortalComplianceRequirement = PortalRequirement;
export interface PortalCompliance {
  vendor: { id: string; name: string };
  asOf: string;
  profile: {
    w9OnFile: boolean;
    w9ReceivedAt: string | null;
    licenseNumber: string | null;
    licenseExpiry: string | null;
    licenseState: PortalRenewalState;
    prequalStatus: string | null;
    prequalExpiry: string | null;
    prequalState: PortalRenewalState;
  };
  certificates: Array<{
    id: string;
    certificateNumber: string;
    carrierName: string;
    policyType: string;
    coverageAmount: string;
    effectiveDate: string;
    expiryDate: string;
    status: string;
    renewalState: PortalRenewalState;
    daysRemaining: number | null;
  }>;
  requirements: PortalRequirement[];
  /** Minimum limits / endorsements Meyer requires that recorded certificates do not meet. */
  coverageGaps?: string[];
  summary: {
    missing: number;
    pendingReview: number;
    expired: number;
    due30: number;
    due60: number;
  };
}
export interface PortalInvoice {
  id: string;
  projectId: string;
  projectName: string;
  invoiceNumber: string;
  status: string;
  invoiceDate: string;
  dueDate: string;
  amount: string;
  balanceDue: string;
  kind: 'RECEIVABLE' | 'PAYABLE';
  collection: { available: boolean; reason: string | null };
}
export interface PortalUnusedInvoiceEvidence {
  id: string;
  projectId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface PortalRecoverableInvoiceEvidence extends PortalUnusedInvoiceEvidence {
  status: 'RECOVERY_REQUIRED';
}

export interface PortalUnusedInvoiceEvidenceList {
  items: PortalUnusedInvoiceEvidence[];
  recoveryItems: PortalRecoverableInvoiceEvidence[];
}

export interface PortalInvoiceEvidenceRevision {
  id: string;
  revision: number;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  checksum: string;
  attachedAt: string;
  projectId: string;
  invoiceNumber: string;
  description: string;
  invoiceDate: string;
  dueDate: string;
  amount: string;
  purchaseOrderId: string | null;
  status: string;
  reviewNote: string | null;
  reviewedAt: string | null;
  validationChecks: Array<{ code: string; status: 'PASSED' | 'REVIEW_REQUIRED'; message: string }>;
}
export interface PortalInvoiceSubmission {
  id: string;
  projectId: string;
  invoiceNumber: string;
  description: string;
  invoiceDate: string;
  dueDate: string;
  amount: string;
  status: string;
  reviewNote: string | null;
  apInvoiceId: string | null;
  createdAt: string;
  updatedAt: string;
  invoiceEvidenceId?: string | null;
  /** Durable original files and the invoice/review snapshot recorded for each revision. */
  invoiceEvidenceHistory?: PortalInvoiceEvidenceRevision[];
  purchaseOrderId?: string | null;
  validationChecks?: Array<{ code: string; status: 'PASSED' | 'REVIEW_REQUIRED'; message: string }>;
}
export interface PortalPayoutRequest {
  id: string;
  apInvoiceId: string;
  projectId: string;
  status: string;
  transferStatus?: string | null;
  reviewNote: string | null;
  createdAt: string;
}
export interface PortalFinance {
  participantType: PortalParticipantType;
  canManagePayments: boolean;
  capabilities: {
    collection: { available: boolean; reason: string | null };
    payoutOnboarding: { available: boolean; reason: string | null };
    payoutRelease: { available: boolean; reason: string | null };
  };
  invoices: PortalInvoice[];
  submissions: PortalInvoiceSubmission[];
  payoutRequests: PortalPayoutRequest[];
  paymentAccount: {
    connected: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    lastVerifiedAt: string | null;
    /** True when the provider could not be reached to verify the account just now. */
    verificationUnavailable: boolean;
  } | null;
}
export interface SubmitPortalInvoiceInput {
  projectId: string;
  invoiceNumber: string;
  description: string;
  invoiceDate: string;
  dueDate: string;
  amount: string;
  invoiceEvidenceId?: string;
  purchaseOrderId?: string;
}

// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------

export interface RequestPortalLinkInput {
  email: string;
  tenantSlug?: string;
}

async function requestLink(
  input: RequestPortalLinkInput,
): Promise<{ accepted: true; message: string }> {
  const tenantSlug =
    input.tenantSlug === undefined ? undefined : normalizePortalTenant(input.tenantSlug);
  if (input.tenantSlug !== undefined && !tenantSlug) {
    throw new PortalApiError('Enter a valid organization code from your invitation.', 400);
  }
  return request('/auth/request-link', {
    method: 'POST',
    body: JSON.stringify({ email: input.email, ...(tenantSlug ? { tenantSlug } : {}) }),
  });
}

async function verify(token: string): Promise<PortalVerifyResult> {
  const result = await request<PortalVerifyResult>('/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  storePortalCsrfToken(result.csrfToken);
  storePortalTenant(result.tenantSlug);
  return result;
}

async function signOut(): Promise<void> {
  await request('/auth/sign-out', { method: 'POST' });
  clearPortalCsrfToken();
}

const me = (): Promise<PortalMe> => request('/me');

const listProjects = (): Promise<{ items: PortalProjectSummary[] }> => request('/projects');

const projectStatus = (projectId: string): Promise<PortalProjectStatus> =>
  request(`/projects/${encodeURIComponent(projectId)}/status`);

const listSelections = (projectId: string): Promise<PortalSelectionList> =>
  request(`/projects/${encodeURIComponent(projectId)}/selections`);

const decisionLog = (projectId: string): Promise<{ items: PortalDecisionLogEntry[] }> =>
  request(`/projects/${encodeURIComponent(projectId)}/decision-log`);

const listMessages = (
  projectId: string,
  before?: string,
): Promise<{ items: PortalMessage[]; nextCursor: string | null }> =>
  request(
    `/projects/${encodeURIComponent(projectId)}/messages${before ? `?before=${encodeURIComponent(before)}` : ''}`,
  );

const sendMessage = (projectId: string, bodyText: string): Promise<PortalMessage> =>
  request(`/projects/${encodeURIComponent(projectId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ bodyText }),
  });

const listDocuments = (projectId: string): Promise<{ items: PortalDocument[] }> =>
  request(`/projects/${encodeURIComponent(projectId)}/documents`);

const downloadDocument = (shareId: string): Promise<PortalDownloadTicket> =>
  request(`/documents/${encodeURIComponent(shareId)}/download`);

const recordDecision = (
  selectionItemId: string,
  input: { selectionOptionId: string; status: 'APPROVED' | 'DECLINED'; comment?: string | null },
): Promise<PortalSelectionList> =>
  request(`/selections/${encodeURIComponent(selectionItemId)}/decision`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

const work = (): Promise<{ items: PortalWorkItem[]; total: number }> => request('/work');
const compliance = (): Promise<PortalCompliance> => request('/compliance');
const downloadRequirement = (id: string): Promise<{ url: string; expiresAt: string }> =>
  request(`/compliance/${encodeURIComponent(id)}/download`);
const finance = (): Promise<PortalFinance> => request('/finance');
const checkoutInvoice = (
  id: string,
): Promise<{
  attemptId: string;
  status: string;
  checkoutUrl: string | null;
  expiresAt: string | null;
}> =>
  request(`/finance/invoices/${encodeURIComponent(id)}/checkout`, { method: 'POST', body: '{}' });
const submitInvoice = (input: SubmitPortalInvoiceInput): Promise<PortalInvoiceSubmission> =>
  request('/finance/submissions', { method: 'POST', body: JSON.stringify(input) });
const payoutOnboarding = (
  input: { returnTo?: 'money' | 'onboarding' | 'packet' } = {},
): Promise<{ url: string }> =>
  request('/finance/payout-onboarding', { method: 'POST', body: JSON.stringify(input) });
const requestPayout = (apInvoiceId: string): Promise<PortalPayoutRequest> =>
  request('/finance/payout-requests', { method: 'POST', body: JSON.stringify({ apInvoiceId }) });

async function uploadInvoiceEvidence(
  projectId: string,
  file: File,
): Promise<{ invoiceEvidenceId: string }> {
  if (
    !['application/pdf', 'image/jpeg', 'image/png'].includes(file.type) ||
    file.size === 0 ||
    file.size > 10 * 1024 * 1024
  ) {
    throw new PortalApiError('Choose a PDF, JPEG or PNG file between 1 byte and 10 MB.', 400);
  }
  const generation = portalSessionGeneration();
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  if (generation !== portalSessionGeneration())
    throw new PortalApiError('Your portal session changed. Sign in again to continue.', 401);
  const sha256 = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  const ticket = await request<{
    invoiceEvidenceId: string;
    url: string;
    requiredHeaders: Record<string, string>;
    expiresAt: string;
    uploadMethod: 'PUT';
    uploadTransport: 'PORTAL_PROXY';
  }>('/finance/invoice-evidence/upload-url', {
    method: 'POST',
    body: JSON.stringify({
      projectId,
      fileName: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      sha256,
    }),
  });
  const path = `/finance/invoice-evidence/${encodeURIComponent(ticket.invoiceEvidenceId)}/upload-file`;
  if (
    ticket.uploadTransport !== 'PORTAL_PROXY' ||
    ticket.uploadMethod !== 'PUT' ||
    ticket.url !== `portal${path}`
  )
    throw new PortalApiError(
      'The invoice upload endpoint could not be verified. Please try again.',
      502,
    );
  try {
    await request(path, { method: 'PUT', body: file, headers: ticket.requiredHeaders });
  } catch (error) {
    if (generation !== portalSessionGeneration())
      throw new PortalApiError('Your portal session changed. Sign in again to continue.', 401);
    if (error instanceof PortalApiError && error.isUnauthenticated) throw error;
    throw new PortalInvoiceUploadUncertainError(
      ticket.invoiceEvidenceId,
      projectId,
      error instanceof Error ? error.message : 'The original upload could not be confirmed.',
      error instanceof PortalApiError ? error.status : 0,
    );
  }
  return { invoiceEvidenceId: ticket.invoiceEvidenceId };
}

const downloadInvoiceEvidence = async (
  submissionId: string,
  evidenceId?: string,
): Promise<{ url: string; fileName?: string; expiresAt: string }> => {
  const selection = evidenceId ? `?evidenceId=${encodeURIComponent(evidenceId)}` : '';
  const ticket = await request<{ url: string; fileName?: string; expiresAt: string }>(
    `/finance/submissions/${encodeURIComponent(submissionId)}/evidence-download${selection}`,
  );
  let destination: URL;
  try {
    destination = new URL(ticket.url);
  } catch {
    throw new PortalApiError(
      'The original invoice download address could not be verified. Please try again.',
      502,
    );
  }
  const localDevelopment =
    import.meta.env.DEV &&
    destination.protocol === 'http:' &&
    ['localhost', '127.0.0.1', '[::1]'].includes(destination.hostname);
  if (
    (destination.protocol !== 'https:' && !localDevelopment) ||
    destination.username ||
    destination.password
  ) {
    throw new PortalApiError(
      'The original invoice download address could not be verified. Please try again.',
      502,
    );
  }
  return ticket;
};

async function submitRequirement(input: {
  requirementId: string;
  version: number;
  file: File;
  expiryDate?: string;
}): Promise<PortalRequirement> {
  const generation = portalSessionGeneration();
  const digest = await crypto.subtle.digest('SHA-256', await input.file.arrayBuffer());
  if (generation !== portalSessionGeneration()) {
    throw new PortalApiError('Your portal session changed. Sign in again to continue.', 401);
  }
  const sha256 = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  const path = `/compliance/${encodeURIComponent(input.requirementId)}`;
  const ticket = await request<{
    documentAssetId: string;
    url: string;
    requiredHeaders: Record<string, string>;
    expiresAt: string;
    version: number;
    uploadMethod: 'PUT';
    uploadTransport: 'PORTAL_PROXY';
  }>(`${path}/upload-url`, {
    method: 'POST',
    body: JSON.stringify({
      fileName: input.file.name,
      mimeType: input.file.type || 'application/octet-stream',
      sizeBytes: input.file.size,
      sha256,
      version: input.version,
    }),
  });
  // The bounded upload proxy retains the portal's session and CSRF protections.
  // Accept only this requirement's API-relative route before sending credentials.
  if (
    ticket.uploadTransport !== 'PORTAL_PROXY' ||
    ticket.uploadMethod !== 'PUT' ||
    ticket.url !== `portal${path}/upload-file`
  ) {
    throw new PortalApiError(
      'The file upload endpoint could not be verified. Please try again.',
      502,
    );
  }
  await request(`${path}/upload-file`, {
    method: ticket.uploadMethod,
    body: input.file,
    headers: ticket.requiredHeaders,
  });
  return request(`${path}/submit`, {
    method: 'POST',
    body: JSON.stringify({
      documentAssetId: ticket.documentAssetId,
      version: ticket.version,
      ...(input.expiryDate ? { expiryDate: input.expiryDate } : {}),
    }),
  });
}

export interface PortalLienWaiver {
  id: string;
  vendorId: string;
  projectId: string;
  apInvoiceId: string;
  jurisdiction: string;
  waiverType: string;
  amount: string;
  throughDate: string;
  lowerTierName: string | null;
  portalRequirementId: string | null;
  status: string;
  templateVersion: string;
  counselReviewRequired: true;
  bodyText: string;
  signedName: string | null;
  signedAt: string | null;
  createdAt: string;
}

const lienWaivers = (): Promise<{ items: PortalLienWaiver[]; notice: string }> =>
  request('/finance/lien-waivers');
const signLienWaiver = (id: string, signedName: string): Promise<PortalLienWaiver> =>
  request(`/finance/lien-waivers/${encodeURIComponent(id)}/sign`, {
    method: 'POST',
    body: JSON.stringify({ signedName, accept: true }),
  });
const addLowerTierWaiver = (
  id: string,
  input: { name: string; amount: string },
): Promise<PortalLienWaiver> =>
  request(`/finance/lien-waivers/${encodeURIComponent(id)}/lower-tier`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const portalClient = {
  lienWaivers,
  signLienWaiver,
  addLowerTierWaiver,
  requestLink,
  verify,
  signOut,
  me,
  listProjects,
  projectStatus,
  listSelections,
  decisionLog,
  listMessages,
  sendMessage,
  listDocuments,
  downloadDocument,
  recordDecision,
  work,
  compliance,
  downloadRequirement,
  submitRequirement,
  finance,
  checkoutInvoice,
  submitInvoice,
  payoutOnboarding,
  requestPayout,
  uploadInvoiceEvidence,
  unusedInvoiceEvidence: () =>
    request<PortalUnusedInvoiceEvidenceList>('/finance/invoice-evidence'),
  recoverInvoiceEvidence: (evidenceId: string) =>
    request<{ invoiceEvidenceId: string; recovered: true }>(
      `/finance/invoice-evidence/${encodeURIComponent(evidenceId)}/recover`,
      { method: 'POST' },
    ),
  downloadInvoiceEvidence,
};
